import get from 'lodash/get'

import router from '@/router'

import * as TYPES from './types'
import { REGISTER_COMPLETION } from '@/store/modules/completion/types'
import { normalizeQuizzes } from '@/services/normalizer'

import { functions } from '@/services/firebase'

export default {
  [TYPES.LOAD_QUIZZES]({ dispatch, commit }, { locale, callback = function () { } }) {
    const loadQuizzes = functions.httpsCallable('loadQuizzes')
    loadQuizzes({ locale }).then(response => {
      commit(TYPES.SET_QUIZZES, response.data)
      dispatch(TYPES.SYNC_QUIZZES, response.data)

      callback(null, true)
    })
  },
  [TYPES.SYNC_QUIZZES]: async ({ commit }, quizPayload) => {
    const normalizedQuizzes = normalizeQuizzes(quizPayload)

    const questionById = normalizedQuizzes.entities.question || {}
    const questionAllIds = Object.keys(questionById)

    commit(TYPES.SET_QUESTIONS, { allIds: questionAllIds, byId: questionById })
  },
  // changes the route based on the next question in the store for the current quiz
  [TYPES.NEXT_QUESTION]({ state, getters, rootState }) {
    const currentRoute = rootState.route
    const quizId = currentRoute.params.quizId
    const contentId = currentRoute.params.contentId
    const contentType = currentRoute.params.contentType
    const quiz = getters.getQuizById(quizId)

    const question = getNextQuestionId(currentRoute, rootState, quiz)

    if (!question) {
      router.push({ name: 'quizResult', params: { quizId, contentId, contentType } })
      return
    }

    router.push({ name: 'quizQuestion', params: { questionId: question.id, quizId, contentId, contentType } })
  },

  // set a response to the current question
  [TYPES.RESPONSE]({ getters, commit, rootState, dispatch }, response) {
    const questionId = rootState.route.params.questionId
    const question = getters.currentQuestion

    dispatch(REGISTER_COMPLETION, {
      contentType: 'quiz',
      id: questionId,
      status: {
        valid: isAnswerValid(question.expectedResponse, response),
        answer: response
      }
    })

    commit(TYPES.SET_RESPONSE, { questionId, response })
  },
  [TYPES.GENERATE_RANDOM_QUIZ]({ commit, rootState }, { contentType, contentId }) {
    const allQuestionIds = getAllQuestionIds(contentType, contentId, rootState)
    const maxNumberOfQuestion = getMaxNumberOfQuestion(contentType, contentId, rootState)
    const questionIds = []
    while (questionIds.length < Math.min(maxNumberOfQuestion, allQuestionIds.length)) {
      const index = Math.round(Math.random() * (allQuestionIds.length - 1))

      const [questionId] = allQuestionIds.slice(index, index + 1)

      if (!questionIds.includes(questionId)) {
        questionIds.push(questionId)
      }
    }

    commit(TYPES.SET_RANDOM_QUIZ_QUESTIONS, { contentId, questionIds })
  }
}

function getNextQuestionId(currentRoute, rootState, quiz) {
  const questions = getRandomQuestionIds(currentRoute.params.contentId, rootState, quiz)

  // if we are not currently on a question, take the first
  if (currentRoute.name !== 'quizQuestion') {
    return questions[0]
  }

  const nextIndex = questions.findIndex(el => el.id === currentRoute.params.questionId) + 1
  // if it was the last question, return null
  if (nextIndex === questions.length) {
    return null
  }

  // default
  return questions[nextIndex]
}

/**
 * @param {Object} question
 * @param {string | number} answer
 * @returns {boolean}
 */
function isAnswerValid(expectedAnswer, answer) {
  if (!expectedAnswer || !answer) return false

  if (['string', 'number'].includes(typeof expectedAnswer)) {
    return expectedAnswer === answer
  }

  if (Array.isArray(expectedAnswer)) {
    // TODO - deep and harsh comparison ?

    // Ensure all expected response are matched
    const expectedMatch = expectedAnswer.every(expected => answer.includes(expected))
    // Ensure there is no "invalid" response checked
    const answerMatch = answer.every(userAnswer => expectedAnswer.includes(userAnswer))

    return expectedMatch && answerMatch
  }

  return Object.keys(expectedAnswer).reduce(
    (correct, property) => correct && isAnswerValid(expectedAnswer[property], answer[property]),
    true
  )
}

/**
 * Restore a previously started quiz funnel data
 * @param {Object} state
 * @param {Function} commit
 * @param {Object} payload
 */
function restoreQuizFunnel(state, commit, payload) {
  if (!payload) return

  // Set the firebase ID
  commit(TYPES.SET_QUIZ_FIREBASE_ID, payload.id)

  // Restore each question
  const { questions } = payload

  Object.keys(questions).forEach(questionId => {
    commit(TYPES.SET_RESPONSE, { questionId, response: questions[questionId].answer })
  })
}

/**
 * Get all the question ids
 * @param {'lessonStep' | 'masterclass' | 'quiz'} contentType
 * @param {string} contentId
 * @param {Object} rootState
 * @returns {string[]}
 */
function getAllQuestionIds(contentType, contentId, rootState) {
  if (contentType === 'quiz') {
    return rootState.quiz.quizzes.find(el => el.id == contentId).questions
  }

  if (contentType === 'lessonStep') {
    const quizId = get(rootState, ['lesson', 'lessonStep', 'byId', contentId, 'quizId'], null)

    if (!quizId) return []

    return rootState.quiz.quizzes.find(el => el.id == quizId).questions
  }

  if (contentType === 'masterclass') {
    const lessonSteps = rootState.theme.themes.find(el => el.id == contentId).lessonSteps
    if (!lessonSteps) return []

    // For each lesson step, get its own quiz question
    const questionIds = lessonSteps.reduce((accumulator, lessonStep) => {
      const subContentQuestionIds = getAllQuestionIds('lessonStep', lessonStep.id, rootState)

      if (subContentQuestionIds.length === 0) return accumulator

      return accumulator.concat(subContentQuestionIds)
    }, [])

    // Remove duplicate
    return Array.from(new Set(questionIds))
  }

  return []
}

/**
 * Get the max number of question
 * @param {'lessonStep' | 'masterclass' | 'quiz'} contentType
 * @param {string} contentId
 * @param {Object} rootState
 * @returns {string[]}
 */
function getMaxNumberOfQuestion(contentType, contentId, rootState) {
  const defaultNumberOfQuestions = 2

  if (contentType === 'quiz') {
    try {
      return rootState.quiz.quizzes.find(el => el.id === contentId).numberOfQuestions
    } catch (e) {
      console.error(e)
      return defaultNumberOfQuestions
    }
  }

  if (contentType === 'lessonStep') {
    const maxQuestionCount = get(rootState, [
      'lesson',
      'lessonStep',
      'byId',
      contentId,
      'numberOfQuestions'
    ])

    return maxQuestionCount || defaultNumberOfQuestions
  }

  if (contentType === 'masterclass') {
    const maxQuestionCount = get(rootState, [
      'lesson',
      'theme',
      'byId',
      contentId,
      'numberOfQuestions'
    ])

    return maxQuestionCount || defaultNumberOfQuestions
  }
}

/**
 * Get the previously generated random question id for the given content
 * @param {string} contentId
 * @param {Object} rootState
 * @param {Object} quiz
 * @returns {string[]}
 */
function getRandomQuestionIds(contentId, rootState, quiz) {
  if (!contentId) return quiz.questions
  return get(rootState, ['quiz', 'randomQuizQuestions', contentId], [])
}

/**
 * Test if the given content type can generate a quiz
 * @param {'lessonStep' | 'masterclass'} contentType
 * @param {string} contentId
 * @param {Object} rootState
 * @returns {boolean}
 */
export function contentHasQuiz(contentType, contentId, rootState) {
  const questionIds = getAllQuestionIds(contentType, contentId, rootState)

  return questionIds.length > 0
}
